import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Target, Heart, Zap, Users, Award, Globe, ArrowRight
} from 'lucide-react';

const VALUES = [
  {
    icon: Target,
    title: 'Excellence',
    description: 'We strive for excellence in every event we touch, from intimate gatherings to large corporate functions.'
  },
  {
    icon: Heart,
    title: 'Passion',
    description: 'Events are our passion. We love bringing people together and creating memorable experiences.'
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: 'We use technology to simplify event planning while maintaining the personal touch that makes events special.'
  },
  {
    icon: Users,
    title: 'Community',
    description: 'We build lasting relationships with our clients, vendors, and staff, creating a community of event professionals.'
  }
];

const STATS = [
  { value: '500+', label: 'Events Completed' },
  { value: '50K+', label: 'Guests Served' },
  { value: '98%', label: 'Satisfaction Rate' },
  { value: '100+', label: 'Vetted Vendors' }
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-foreground py-16 -mt-20 pt-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">About Eventism</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Your trusted partner in creating exceptional events in the Denver metro area and beyond.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Our Story */}
        <div className="py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Eventism was born from a simple idea: event planning should be easy, transparent, and enjoyable. 
                  We saw how stressful and time-consuming it was for people to coordinate multiple vendors, 
                  compare prices, and manage all the details that go into creating a memorable event.
                </p>
                <p>
                  Based in Denver, Colorado, we've built a platform that connects event planners with 
                  vetted, high-quality service providers. From catering and entertainment to staffing 
                  and logistics, we offer everything you need to bring your vision to life.
                </p>
                <p>
                  Our mission is to take the complexity out of event planning while ensuring every 
                  detail is handled with care and professionalism.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-8 text-foreground">
              <Award className="h-12 w-12 mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-2">Our Promise</h3>
              <p className="text-foreground/80">
                We stand behind every event we help create. Our commitment to quality, 
                reliability, and customer satisfaction is at the heart of everything we do.
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="py-12 border-t">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-bold text-indigo-500 mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="py-12 border-t">
          <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {VALUES.map((value, idx) => {
              const Icon = value.icon;
              return (
                <Card key={idx}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-indigo-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-2">{value.title}</h3>
                        <p className="text-muted-foreground">{value.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Service Area */}
        <div className="py-12 border-t">
          <div className="text-center">
            <Globe className="h-12 w-12 text-indigo-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Where We Serve</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              We proudly serve the greater Denver metropolitan area, including Boulder, 
              Aurora, Lakewood, Centennial, and surrounding communities. Our network of 
              vendors and staff allows us to deliver exceptional events throughout Colorado.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="py-12">
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-foreground border-0">
            <CardContent className="py-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Let's Create Something Amazing</h2>
              <p className="text-foreground/80 mb-8 max-w-xl mx-auto">
                Whether you're planning a corporate event, wedding, or private party, 
                we're here to help make it unforgettable.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  className="bg-white text-indigo-600 hover:bg-white/90"
                  onClick={() => navigate('/plan-event')}
                >
                  Plan Your Event <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-foreground hover:bg-white/10"
                  onClick={() => navigate('/contact')}
                >
                  Contact Us
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
